/**
 * `ConfidenceBadge` — the four-axis model made visible.
 *
 * `PROJECT_STRUCTURE.md` names this component explicitly, and says why: *"if either is
 * ever bypassed by a feature rendering a raw number, the product has quietly
 * regressed."*
 *
 * So it renders the Admiralty pair (`B2`) and the extraction confidence **separately**,
 * and there is no prop that would blend them. ADR-0016 keeps the axes apart in storage
 * because a source graded A reporting something nothing else confirms is a different
 * situation from a source graded C reporting something three others agree with — and one
 * number cannot say which you are looking at.
 *
 * The title text spells out both grades in words. A reviewer deciding a merge should not
 * have to remember what `D4` means.
 */

import type { ReactElement } from 'react';
import {
  CREDIBILITY_MEANING,
  RELIABILITY_MEANING,
  admiraltyCode,
  credibilityTone,
  reliabilityTone,
} from '@/lib/confidence';

const TONE_CLASS: Record<string, string> = {
  ok: 'text-ok border-ok/40 bg-ok/10',
  warn: 'text-warn border-warn/40 bg-warn/10',
  danger: 'text-danger border-danger/40 bg-danger/10',
  muted: 'text-muted border-line bg-raised',
};

export interface ConfidenceBadgeProps {
  /** Admiralty source reliability, A–F. */
  sourceReliability: string;
  /** Admiralty information credibility, 1–6. Absent means 6 (ADR-0030). */
  informationCredibility?: number | null;
  /** How certain the extractor was, 0–1. Shown beside, never blended in. */
  extractionConfidence?: number | null;
  /** Match probability, when this describes an entity rather than an assertion. */
  matchProbability?: number | null;
  /** Compact form, for a dense table. Still shows both Admiralty axes. */
  compact?: boolean;
}

/**
 * Render the confidence axes for one claim.
 *
 * @param props - The axes to display.
 * @returns The badge.
 */
export function ConfidenceBadge({
  sourceReliability,
  informationCredibility,
  extractionConfidence,
  matchProbability,
  compact = false,
}: ConfidenceBadgeProps): ReactElement {
  // Absence means 6 — "truth cannot be judged" — never zero, and never blank. A blank
  // would read as "no opinion", which is a different and more reassuring claim.
  const credibility = informationCredibility ?? 6;
  const code = admiraltyCode(sourceReliability, credibility);
  const tone =
    reliabilityTone(sourceReliability) === 'danger' || credibilityTone(credibility) === 'danger'
      ? 'danger'
      : reliabilityTone(sourceReliability) === 'warn' || credibilityTone(credibility) === 'warn'
        ? 'warn'
        : reliabilityTone(sourceReliability);

  const title = [
    `Source reliability ${sourceReliability}: ${RELIABILITY_MEANING[sourceReliability as 'A'] ?? 'Unknown'}`,
    `Information credibility ${credibility}: ${CREDIBILITY_MEANING[credibility as 1] ?? 'Unknown'}`,
    extractionConfidence != null
      ? `Extraction confidence ${extractionConfidence.toFixed(2)}`
      : null,
    matchProbability != null ? `Match probability ${matchProbability.toFixed(2)}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <span className="inline-flex items-center gap-1.5 align-middle" title={title}>
      <span
        data-testid="admiralty-code"
        className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-xs font-medium ${TONE_CLASS[tone]}`}
      >
        {code}
      </span>
      {!compact && extractionConfidence != null && (
        <span data-testid="extraction-confidence" className="font-mono text-xs text-muted">
          {extractionConfidence.toFixed(2)}
        </span>
      )}
      {!compact && matchProbability != null && (
        <span data-testid="match-probability" className="font-mono text-xs text-muted">
          m{matchProbability.toFixed(2)}
        </span>
      )}
    </span>
  );
}
